import React from "react";
import { Column, Loader, Row, Sheet, Text } from "@amzn/meridian";
import { InspectionResult } from "src/services/inspectionService";
import "./ComplianceDetailSheet.scss";
import ThemedTag from "./ThemedTag";
import { isResultNA } from "src/components/pages/inspection/utils";

interface ComplianceDetailSheetProps {
    open: boolean;
    onClose: () => void;
    result: InspectionResult | null;
    presignedUrlMap: Record<string, string>;
    onOverride: (result: InspectionResult) => void;
}

const ComplianceDetailSheet: React.FC<ComplianceDetailSheetProps> = ({
    open,
    onClose,
    result,
    presignedUrlMap,
    onOverride,
}) => {
    if (!result) return null;

    const evidenceUrls = result.evidence
        .map((e) => presignedUrlMap[e.uri])
        .filter(Boolean);

    const isAnalysisFailed = result.aiResult?.toUpperCase() === "FAILED";
    const isAnalysisPending = result.aiResult?.toUpperCase() === "PENDING";
    const isNA = isResultNA(result);

    const themedTagText = isAnalysisPending ? "Analyzing..."
        : isAnalysisFailed ? "Failed"
            : isNA ? "N/A"
                : result.isCompliant ? "Compliant"
                    : "Non Compliant";

    const themedTagType = isAnalysisPending ? "neutral"
        : isNA ? "gray"
            : result.isCompliant ? "success"
                : "error";

    const canOverride = !isAnalysisPending && !isAnalysisFailed && !isNA;

    return (
        <Sheet type="overlay" side="bottom" open={open} onClose={onClose} className="compliance-detail-sheet">
            <Column spacing="400" spacingInset="500">
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Text type="h300">Compliance Details</Text>
                </Row>

                {result.objectId !== null && (
                    <Text type="h200" color="primary">
                        {result.objectId}
                    </Text>
                )}

                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Column spacing="100">
                        <Text type="b200" color="primary">
                            Answer : {result.confirmedResult}
                        </Text>
                        {result.isInspectorOverride && (
                            <Text type="b100" color="secondary">
                                (Manually Overridden)
                            </Text>
                        )}
                    </Column>
                    <ThemedTag text={themedTagText} type={themedTagType} />
                </Row>

                {isAnalysisPending ? (
                    <Loader type="circular" size="large" />
                ) : (
                    <>
                        {result.aiReasoning && result.aiReasoning !== "" && (
                            <Column spacing="100">
                                <Text type="b100" color="secondary">Reason</Text>
                                <Text type="b200" color="primary">{result.aiReasoning}</Text>
                            </Column>
                        )}

                        {evidenceUrls.length > 0 && (
                            <Row spacing="300" wrap="down">
                                {evidenceUrls.map((url, idx) => (
                                    <img
                                        key={idx}
                                        src={url}
                                        alt={`Evidence ${idx + 1}`}
                                        className="compliance-detail-sheet__image"
                                    />
                                ))}
                            </Row>
                        )}

                        {canOverride && (
                            <button
                                className="compliance-detail-sheet__override-btn"
                                onClick={() => onOverride(result)}
                            >
                                MANUAL OVERRIDE
                            </button>
                        )}
                    </>
                )}
            </Column>
        </Sheet>
    );
};

export default ComplianceDetailSheet;
